import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertCircle, Building2, Loader2, Globe, Mail, Phone, MapPin } from "lucide-react";
import { CompanyAdmin, CompanyAdminInput, isValidEmail, isValidUrl } from "@/types/crm";
import { CompanyLogoUploader } from "./CompanyLogoUploader";
import "flag-icons/css/flag-icons.min.css";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { countries, countryCodeMap } from "@/data/countries";

interface CompanyAdminModalProps { isOpen: boolean; onClose: () => void; onSave: (company: CompanyAdminInput) => void; company: CompanyAdmin | null;
}

export function CompanyAdminModal({  isOpen,  onClose,  onSave,  company
}: CompanyAdminModalProps) {
  const [formData, setFormData] = useState<CompanyAdminInput>({
    name: "",
    descripcion: "",
    logo_url: "",
    direccion: "",
    telefono: "",
    email: "",
    sitio_web: "",
    estado: 1
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [countryCode, setCountryCode] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (company) {
      setFormData({
        name: company.name,
        descripcion: company.descripcion || "",
        logo_url: company.logo_url || "",
        direccion: company.direccion || "",
        telefono: company.telefono || "",
        email: company.email || "",
        sitio_web: company.sitio_web || "",
        estado: company.estado
      });
      
      if (company.telefono) {
        const phoneNumber = parsePhoneNumberFromString(company.telefono);
        if (phoneNumber) {
          const country = countries.find(c => c.code === phoneNumber.countryCallingCode);
          if (country) {
            setCountryCode(country.code);
            setSearchTerm(`${country.name} (${country.code})`);
          }
        }
      }
    } else {
      setFormData({
        name: "",
        descripcion: "",
        logo_url: "",
        direccion: "",
        telefono: "",
        email: "",
        sitio_web: "",
        estado: 1
      });
      setCountryCode("");
      setSearchTerm("");
    }
    setErrors({});
  }, [company, isOpen]);

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.nombre = "El nombre de la empresa es obligatorio";
    } else if (formData.name.trim().length < 3) {
      newErrors.nombre = "El nombre debe tener al menos 3 caracteres";
    } else if (formData.name.trim().length > 255) {
      newErrors.nombre = "El nombre no puede exceder los 255 caracteres";
    }

    if (formData.email && formData.email.trim()) {
      if (!isValidEmail(formData.email)) {
        newErrors.email = "Formato de correo inválido";
      } else if (formData.email.length > 255) {
        newErrors.email = "El email no puede exceder los 255 caracteres";
      }
    }

    if (formData.sitio_web && formData.sitio_web.trim()) {
      const url = formData.sitio_web.startsWith('http') 
        ? formData.sitio_web 
        : `https://${formData.sitio_web}`;
      if (!isValidUrl(url)) {
        newErrors.sitio_web = "URL inválida (ej: www.empresa.com)";
      } else if (formData.sitio_web.length > 255) {
        newErrors.sitio_web = "El sitio web no puede exceder los 255 caracteres";
      }
    }

    if (formData.telefono && formData.telefono.trim()) {
      if (countryCode) {
        const country = countries.find(c => c.code === countryCode);
        if (country) {
          const libPhoneCountry = countryCodeMap[country.flagCode];
          const phoneNumber = parsePhoneNumberFromString(formData.telefono, libPhoneCountry as any);
          if (!phoneNumber || !phoneNumber.isValid()) {
            newErrors.telefono = "Número inválido para el país seleccionado";
          }
        }
      } else {
        newErrors.telefono = "Seleccione un país para validar el teléfono";
      }
      
      if (formData.telefono.length > 255) {
        newErrors.telefono = "El teléfono no puede exceder los 255 caracteres";
      }
    }

    if (formData.direccion && formData.direccion.length > 255) {
      newErrors.direccion = "La dirección no puede exceder los 255 caracteres";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    
    try {
      const normalizedData = {
        ...formData,
        sitio_web: formData.sitio_web && formData.sitio_web.trim()
          ? (formData.sitio_web.startsWith('http') 
              ? formData.sitio_web 
              : `https://${formData.sitio_web}`)
          : ""
      };

      await onSave(normalizedData);
      onClose();
    } catch (error) {
      console.error("Error al guardar empresa:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: keyof CompanyAdminInput, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const handleCountryInputChange = (value: string) => {
    setSearchTerm(value);
    setShowDropdown(true);
  };

  const filteredCountries = countries.filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.code.includes(searchTerm)
  );

  const handleSelectCountry = (country: typeof countries[0]) => {
    setCountryCode(country.code);
    setSearchTerm(`${country.name} (${country.code})`);
    setShowDropdown(false);
    
    if (errors.telefono) {
      setErrors(prev => ({ ...prev, telefono: "" }));
    }
  };

  const handlePhoneChange = (value: string) => {
    const cleanedValue = value.replace(/[^\d+\-\s()]/g, "");
    handleChange("telefono", cleanedValue);
  };

  const selectedCountry = countries.find(c => c.code === countryCode);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center text-2xl">
            <Building2 className="h-6 w-6 mr-2 text-blue-600" />
            {company ? "Editar Empresa" : "Crear Nueva Empresa"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <CompanyLogoUploader
            value={formData.logo_url}
            onChange={(url) => handleChange("logo_url", url)}
            companyName={formData.name}
            error={errors.logo_url}
          />

          <div className="space-y-4">
            <div>
              <Label htmlFor="nombre">Nombre de la Empresa *</Label>
              <Input
                id="nombre"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                placeholder="TechCorp Solutions"
                className="mt-1"
                disabled={isSubmitting}
                maxLength={255}
              />
              {errors.nombre && (
                <p className="text-red-500 text-sm mt-1 flex items-center">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  {errors.nombre}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="descripcion">Descripción</Label>
              <Textarea
                id="descripcion"
                value={formData.descripcion}
                onChange={(e) => handleChange("descripcion", e.target.value)}
                placeholder="Breve descripción de la empresa..."
                rows={3}
                className="mt-1"
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="email">Correo Electrónico</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  placeholder="info@empresa.com"
                  className="mt-1 pl-10"
                  disabled={isSubmitting}
                  maxLength={255}
                />
              </div>
              {errors.email && (
                <p className="text-red-500 text-sm mt-1 flex items-center">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  {errors.email}
                </p>
              )}
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="telefono">Teléfono</Label>
              <div className="flex gap-2 relative">
                <div className="relative w-40">
                  <Input
                    ref={inputRef}
                    value={searchTerm}
                    onChange={(e) => handleCountryInputChange(e.target.value)}
                    placeholder="Buscar país o código"
                    className="mt-1"
                    disabled={isSubmitting}
                  />
                  {showDropdown && (
                    <div className="absolute z-50 bg-white border mt-1 max-h-60 overflow-y-auto w-full shadow-lg rounded">
                      {filteredCountries.map(c => (
                        <div
                          key={c.code}
                          className="cursor-pointer px-2 py-1 hover:bg-gray-100 flex items-center gap-2"
                          onClick={() => handleSelectCountry(c)}
                        >
                          <span className={`fi fi-${c.flagCode} w-6 h-4`}></span>
                          <span>{c.name} ({c.code})</span>
                        </div>
                      ))}
                      {filteredCountries.length === 0 && (
                        <div className="px-2 py-1 text-gray-400">No se encontró ningún país</div>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex-1 relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="telefono"
                    type="tel"
                    value={formData.telefono}
                    onChange={(e) => handlePhoneChange(e.target.value)}
                    placeholder={selectedCountry ? selectedCountry.example : "Número de teléfono"}
                    className="flex-1 pl-10"
                    disabled={isSubmitting}
                    maxLength={255}
                  />
                </div>
              </div>
              {errors.telefono && (
                <p className="text-red-500 text-sm mt-1 flex items-center">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  {errors.telefono}
                </p>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="direccion">Dirección</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Textarea
                  id="direccion"
                  value={formData.direccion}
                  onChange={(e) => handleChange("direccion", e.target.value)}
                  placeholder="Dirección completa de la empresa"
                  rows={2}
                  className="mt-1 pl-10"
                  disabled={isSubmitting}
                  maxLength={255}
                />
              </div>
              {errors.direccion && (
                <p className="text-red-500 text-sm mt-1 flex items-center">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  {errors.direccion}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="sitio_web">Sitio Web</Label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="sitio_web"
                  type="url"
                  value={formData.sitio_web}
                  onChange={(e) => handleChange("sitio_web", e.target.value)}
                  placeholder="www.empresa.com"
                  className="mt-1 pl-10"
                  disabled={isSubmitting}
                  maxLength={255}
                />
              </div>
              {errors.sitio_web && (
                <p className="text-red-500 text-sm mt-1 flex items-center">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  {errors.sitio_web}
                </p>
              )}
              <p className="text-xs text-slate-500 mt-1">
                Puedes ingresar con o sin https://
              </p>
            </div>
          </div>

          {company && (
            <div>
              <Label>Estado de la Empresa</Label>
              <Select 
                value={formData.estado.toString()} 
                onValueChange={(value) => handleChange("estado", parseInt(value))}
                disabled={isSubmitting}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Activa</SelectItem>
                  <SelectItem value="0">Inactiva</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button 
            type="button" 
            variant="outline" 
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Guardando...
              </>
            ) : (
              company ? "Actualizar Empresa" : "Crear Empresa"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}